import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ProgressBar from 'src/components/ui/Features/ProgressBar/ProgressBar';
import { useLearning } from 'src/hook/data/useLearning';
import { useLesson } from 'src/hook/data/useLesson';
import { useScenario } from 'src/hook/data/useScenario';
import useUser from 'src/hook/data/useUser';
import { LanguageLevel } from 'src/model/enum/enums';
import { LessonState } from 'src/model/Lessons';
import { IconLocation, ScenariosLocation } from 'src/utils/URLUtils';
import styles from './LessonSection.module.scss';

export const LessonSection = () => {
    const { t, i18n } = useTranslation();
    const { getAllLessons, scenarios, scenarioQAs, fetchScenarioData } = useLesson(false);
    const [incompleteLessons, setIncompleteLessons] = useState<LessonState[]>([]);
    const navigate = useNavigate();
    const { learningInfo } = useLearning();
    const { getAllScenarios } = useScenario(false);
    const { userInfo } = useUser();
    const isLesson = useMemo(() => {
        return userInfo?.LanguageLevel === LanguageLevel.Beginner ||
            userInfo?.LanguageLevel === LanguageLevel.Basic;
    }, [userInfo?.LanguageLevel]);


    const handleClick = (questionId: string) => {
        if (questionId === 'more') {
            navigate(isLesson ? '/lessons' : '/scenario');
        } else {
            navigate(isLesson ? `/lessons/lesson?id=${questionId}` : `/scenario/category?id=${questionId}`);
        }
    }

    const fetchIncompleteLessons = useCallback(async () => {
        const lessons = isLesson ? await getAllLessons(5) : await getAllScenarios(5);
        setIncompleteLessons(lessons.filter(l => l.IsCompleted === false) || []);
    }, [getAllLessons, getAllScenarios, isLesson]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!scenarios || !scenarioQAs) {
                    await fetchScenarioData();
                }
                if (incompleteLessons.length === 0 && learningInfo?.Lessons) {
                    await fetchIncompleteLessons();
                }
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
    }, [fetchIncompleteLessons, fetchScenarioData, incompleteLessons.length,
        learningInfo?.Lessons, scenarios, scenarioQAs]);

    useEffect(() => {
        // Reset incomplete lessons when isLesson changes
        setIncompleteLessons([]);
        fetchIncompleteLessons();
    }, [isLesson, fetchIncompleteLessons]);


    return (
        <section className={styles.container}>
            <h2 className='homeTitle'>{isLesson ? t('Lessons') : t('Scenarios')}</h2>
            <p dir={i18n.dir()} className='homeSubtitle'>{isLesson ?
                t('Beginner-friendly lessons, step by step.') :
                t('Practice using words in different real-life situations.')}</p>

            <div className={styles.grid}>
                {incompleteLessons.map((lesson) => (
                    <div key={lesson.QuestionId}
                        onClick={() => handleClick(lesson.QuestionId)}
                        className={styles.card}>
                        <img
                            src={ScenariosLocation(lesson.QuestionId)}
                            alt={lesson.QuestionId} />
                        <span>{t(lesson.Name)}</span>
                        <div className={styles.progressBar}>
                            <ProgressBar
                                knownCount={lesson.CompletedCount}
                                totalCount={lesson.TotalCount}
                            />
                        </div>
                    </div>
                ))}
                <div className={styles.card}
                    onClick={() => handleClick('more')}>
                    <img src={IconLocation('celeb-more')} alt={t('More')} />
                    <span>{t('More')}</span>
                </div>
            </div>
        </section>
    );
};
